import React, { useState, useEffect } from 'react';
import { Toast } from '../../../components/common/Toast';
import SectionCard from '../../../components/common/SectionCard';
import { PopupConfigurationcon } from '../../../components/common/SvgIcon';
import ToggleField from '../../../components/common/ToggleField';
import InputField from '../../../components/common/InputField';


const save = `${LEGAL_PAGES.assets_url}/images/icons/save.png`;
const reset = `${LEGAL_PAGES.assets_url}/images/icons/reset.png`;

const PopupSettings = ({isLoading, setIsLoading}) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    disable_popup_title: false,
    popup_notice_title: '',
    agreement_text: '',
    accept_button_text: '',
    popup_width: '',
    popup_height: '',
    stop_showing_popup: false,
    disable_popup_js_css: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/popup-settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        setFormData(result.data);
      }
    } catch (error) {
      console.error('Error fetching popup settings:', error);
      Toast.error('Failed to load popup settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${LEGAL_PAGES.apiUrl}/popup-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': LEGAL_PAGES.nonce,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        Toast.success('Popup settings saved successfully!');
        if (result.data) {
          setFormData(result.data);
        }
      } else {
        Toast.error('Failed to save popup settings.');
      }
    } catch (error) {
      console.error('Error saving popup settings:', error);
      Toast.error('An error occurred while saving popup settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFormData({
      disable_popup_title: false,
      popup_notice_title: '',
      agreement_text: '',
      accept_button_text: '',
      popup_width: '',
      popup_height: '',
      stop_showing_popup: false,
      disable_popup_js_css: false,
    });
    Toast.info('Fields reset successfully!');
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-6 mt-6">
        {isLoading ? (
          <SectionCard title="Popup Configuration" icon={<PopupConfigurationcon />}>
            <div className="space-y-4">
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard maxHeight="max-h-[736px]" title="Popup Configuration" icon={<PopupConfigurationcon />}>
            <div className="space-y-6">
              <ToggleField
                label="Disable Popup Notice Title"
                description="Hide the popup title"
                name="disable_popup_title"
                checked={formData.disable_popup_title}
                onChange={(name, value) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Popup Notice Title"
                  name="popup_notice_title"
                  value={formData.popup_notice_title}
                  onChange={handleInputChange}
                />
                <InputField
                  label="Agreement Text"
                  name="agreement_text"
                  value={formData.agreement_text}
                  onChange={handleInputChange}
                />
                <InputField
                  label="Accept Button Text"
                  name="accept_button_text"
                  value={formData.accept_button_text}
                  onChange={handleInputChange}
                />
                <InputField
                  type="number"
                  label="Popup Width (px)"
                  name="popup_width"
                  value={formData.popup_width}
                  onChange={handleInputChange}
                />

                <InputField
                  type="number"
                  label="Popup Height (px)"
                  name="popup_height"
                  value={formData.popup_height}
                  onChange={handleInputChange}
                />
              </div>
              <ToggleField
                label="Stop Showing Popup Once Accepted"
                description="Prevent popup from showing again if accepted"
                name="stop_showing_popup"
                checked={formData.stop_showing_popup}
                onChange={(name, value) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />

              <ToggleField
                label="Disable Popup JS & CSS"
                description="Disable if your theme already has Bootstrap"
                name="disable_popup_js_css"
                checked={formData.disable_popup_js_css}
                onChange={(name, value) =>
                  setFormData((prev) => ({ ...prev, [name]: value }))
                }
              />
            </div>
          </SectionCard>
        )}

        <div className="flex items-center justify-start gap-2 rounded-xl p-4">
          <button
            type="submit"
            disabled={saving}
            className={[
              'inline-flex items-center cursor-pointer justify-center rounded-xl px-6 py-[14px] text-sm font-semibold text-white shadow-[0px_4px_14px_-2px_#7C3BED40] transition',
              saving ? 'cursor-not-allowed bg-indigo-400' : 'bg-[#7C3BED] hover:bg-indigo-700',
            ].join(' ')}
          >
            <img src={save} alt="Save Icon" className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>

          <button
            type="button"
            onClick={handleReset}
            className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-[#E4E4E7] bg-white px-6 py-[14px] text-sm font-semibold text-[#17171C] transition hover:bg-gray-50"
          >
            <img src={reset} alt="Reset Icon" className="mr-2 h-4 w-4" />
            Reset Fields
          </button>
        </div>
      </div>
    </form>
  );
};

export default PopupSettings;